import { EditionType, getEditionTypeString } from '../../_main/ClientPhotoStudio/ClientPhotoStudio.context'
import { GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'

import { AnalyticsEvent } from 'utils/analytics'
import { MUIDivider } from 'components/common/MUIDivider'
import React from 'react'
import RedoIcon from '@mui/icons-material/Redo'
import Stack from '@mui/material/Stack'
import UndoIcon from '@mui/icons-material/Undo'
import { useClientPhotoStudio } from '../../_main/ClientPhotoStudio/ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

export interface UndoRedoPanelProps {
  /** Optional custom label for undo action */
  undoLabel?: string
  /** Optional custom label for redo action */
  redoLabel?: string
  /** Optional tooltip for undo button */
  undoTooltip?: string
  /** Optional tooltip for redo button */
  redoTooltip?: string
  /** Custom styling options */
  sx?: React.CSSProperties
  /** Whether the panel is disabled */
  disabled?: boolean
  /** Analytics logging function */
  logPhotoStudioEvent?: (eventName: string, params?: {}) => void
  /** Assignment ID for analytics */
  assignmentId?: string
  /** Source visual filename for analytics */
  sourceVisualFilename?: string
  /** Whether undo is enabled */
  canUndo?: boolean
  /** Whether redo is enabled */
  canRedo?: boolean
  /** Callback when undo is clicked */
  onUndo?: () => void
  /** Callback when redo is clicked */
  onRedo?: () => void
  /** Current history entry for analytics (to get generation sequence number) */
  currentHistoryEntry?: { imageGenerationSequenceNumber?: number }
  /** Whether to use compact styling (for Pintura toolbar) */
  compact?: boolean
}

/**
 * @component
 * Simple Undo/Redo panel with just UI controls.
 * This component only handles the visual presentation and user interactions.
 * History logic is handled by the parent component via props.
 */
export const UndoRedoPanel: React.FC<UndoRedoPanelProps> = ({
  undoTooltip,
  redoTooltip,
  sx,
  disabled = false,
  logPhotoStudioEvent,
  assignmentId,
  sourceVisualFilename,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  currentHistoryEntry,
  compact = false,
}) => {
  const { t } = useTranslation(['common'])
  const { isOriginalVisualType, getCommonAnalyticsData, selectedAIFeature, selectedPinturaFeature } = useClientPhotoStudio()

  // Handle undo action
  const handleUndo = () => {
    if (onUndo) {
      onUndo()

      // Log analytics for undo
      if (logPhotoStudioEvent && getCommonAnalyticsData) {
        const editionType = selectedAIFeature === 'virtual_staging' ? EditionType.VIRTUAL_STAGING :
          selectedAIFeature === 'magic_eraser' ? EditionType.MAGIC_ERASER :
            selectedAIFeature === 'enhance' ? EditionType.ENHANCE :
              selectedPinturaFeature ? EditionType.ADJUST : undefined

        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO_REDO, {
            action: 'undo',
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            ...commonData,
          })
          // Also log the old event for backward compatibility
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO, {
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            ...commonData,
          })
        }).catch(() => {
          // Fallback if metadata extraction fails
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO_REDO, {
            action: 'undo',
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            sourceVisualFilename,
            assignmentId,
          })
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO, {
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            sourceVisualFilename,
            assignmentId,
          })
        })
      } else if (logPhotoStudioEvent) {
        // Fallback for when getCommonAnalyticsData is not available
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO, {
          assignmentId,
          sourceVisualFilename,
        })
      }
    }
  }

  // Handle redo action
  const handleRedo = () => {
    if (onRedo) {
      onRedo()

      // Log analytics for redo
      if (logPhotoStudioEvent && getCommonAnalyticsData) {
        const editionType = selectedAIFeature === 'virtual_staging' ? EditionType.VIRTUAL_STAGING :
          selectedAIFeature === 'magic_eraser' ? EditionType.MAGIC_ERASER :
            selectedAIFeature === 'enhance' ? EditionType.ENHANCE :
              selectedPinturaFeature ? EditionType.ADJUST : undefined

        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO_REDO, {
            action: 'redo',
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            ...commonData,
          })
          // Also log the old event for backward compatibility
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_REDO, {
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            ...commonData,
          })
        }).catch(() => {
          // Fallback if metadata extraction fails
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_UNDO_REDO, {
            action: 'redo',
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            sourceVisualFilename,
            assignmentId,
          })
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_REDO, {
            editionType: getEditionTypeString(editionType, selectedPinturaFeature),
            image_generation_sequence_number: currentHistoryEntry?.imageGenerationSequenceNumber,
            sourceVisualFilename,
            assignmentId,
          })
        })
      } else if (logPhotoStudioEvent) {
        // Fallback for when getCommonAnalyticsData is not available
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_REDO, {
          assignmentId,
          sourceVisualFilename,
        })
      }
    }
  }

  // Generate tooltips based on actions if not provided
  const finalUndoTooltip = undoTooltip || t('undo')
  const finalRedoTooltip = redoTooltip || t('redo')

  const isUndoEnabled = canUndo && !disabled && !isOriginalVisualType
  const isRedoEnabled = canRedo && !disabled && !isOriginalVisualType

  return (
    <Stack
      flexDirection="row"
      justifyContent="center"
      alignItems="center"
      margin={compact ? 0 : '2.4rem 0'}
      className={compact ? 'pintura-undo-redo-wrapper' : undefined}
      sx={{
        ...sx,
        // Override Pintura button hover effects (only when compact)
        ...(compact && {
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
          backgroundImage: 'none !important',
          '& button': {
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
            backgroundImage: 'none !important',
            '&::before': {
              display: 'none !important',
            },
            '&::after': {
              display: 'none !important',
            },
            '&:hover': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
            },
          },
          // Remove any circle/ripple effects
          '& *': {
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
            backgroundImage: 'none !important',
            '&::before': {
              display: 'none !important',
            },
            '&::after': {
              display: 'none !important',
            },
            '&:hover': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
            },
          },
        }),
      }}
    >
      <Stack
        flexDirection="row"
        height={compact ? '2.4rem' : '3.2rem'}
        alignItems="center"
        borderRadius="3rem"
        padding={0} // each half carries the padding so the whole capsule is clickable
        overflow="hidden"
        border={`1px solid ${GRAY_900}`}
        sx={{
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'default',
          backgroundColor: 'transparent !important',
          background: 'transparent !important',
          // Remove Pintura button hover circle and background (only when compact)
          ...(compact && {
            '&::before': {
              display: 'none !important',
            },
            '&::after': {
              display: 'none !important',
            },
            // Override any Pintura button styles including hover background
            '&:hover': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
              '&::before': {
                display: 'none !important',
              },
              '&::after': {
                display: 'none !important',
              },
            },
            '&:active': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
            },
            '&:focus': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
            },
            // Override any nested button styles from Pintura
            '& button': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              backgroundImage: 'none !important',
              '&:hover': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
                backgroundImage: 'none !important',
              },
              '&:active': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
                backgroundImage: 'none !important',
              },
            },
          }),
        }}
      >
        <Stack
          onClick={isUndoEnabled ? handleUndo : undefined}
          role="button"
          aria-disabled={!isUndoEnabled}
          height="100%"
          alignItems="center"
          justifyContent="center"
          sx={{
            padding: compact ? '0 .6rem 0 .8rem' : '0 1rem 0 1.6rem',
            cursor: isUndoEnabled ? 'pointer' : 'not-allowed',
            '&:hover': isUndoEnabled ? { opacity: 0.8 } : {},
            // Remove any hover effects from Pintura (only when compact)
            ...(compact && {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              '&::before': {
                display: 'none !important',
              },
              '&::after': {
                display: 'none !important',
              },
              '&:hover': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
                opacity: isUndoEnabled ? 0.8 : 1,
              },
              '&:active': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
              },
            }),
          }}
        >
          <UndoIcon
            sx={{
              color: isUndoEnabled ? WHITE : GRAY_800,
              ...(compact && { fontSize: '1.8rem' }),
            }}
            titleAccess={finalUndoTooltip}
          />
        </Stack>

        <MUIDivider
          orientation="vertical"
          margin={2}
          color={GRAY_800}
        />

        <Stack
          onClick={isRedoEnabled ? handleRedo : undefined}
          role="button"
          aria-disabled={!isRedoEnabled}
          height="100%"
          alignItems="center"
          justifyContent="center"
          sx={{
            padding: compact ? '0 .8rem 0 .6rem' : '0 1.6rem 0 1rem',
            cursor: isRedoEnabled ? 'pointer' : 'not-allowed',
            '&:hover': isRedoEnabled ? { opacity: 0.8 } : {},
            // Remove any hover effects from Pintura (only when compact)
            ...(compact && {
              backgroundColor: 'transparent !important',
              background: 'transparent !important',
              '&::before': {
                display: 'none !important',
              },
              '&::after': {
                display: 'none !important',
              },
              '&:hover': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
                opacity: isRedoEnabled ? 0.8 : 1,
              },
              '&:active': {
                backgroundColor: 'transparent !important',
                background: 'transparent !important',
              },
            }),
          }}
        >
          <RedoIcon
            sx={{
              color: isRedoEnabled ? WHITE : GRAY_800,
              ...(compact && { fontSize: '1.8rem' }),
            }}
            titleAccess={finalRedoTooltip}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
